import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

const DROPDOWN_WIDTH = 180; // px — matches min-width in CSS

const ActionMenu = ({ children, trigger }) => {
  const [open, setOpen] = useState(false);
  // ready: false = render hidden so we can measure; true = visible at final position
  const [coords, setCoords] = useState({ top: 0, left: 0, ready: false });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Step 1: when opening, place dropdown tentatively below trigger (hidden)
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(
      rect.right - DROPDOWN_WIDTH,
      window.innerWidth - DROPDOWN_WIDTH - 8
    ));
    setCoords({ top: rect.bottom + 4, left, ready: false });
  }, [open]);

  // Step 2: after dropdown renders (hidden), measure its actual height and flip if needed
  // useLayoutEffect fires before paint so there is no visible flash
  useLayoutEffect(() => {
    if (!open || !dropdownRef.current || coords.ready) return;
    const dropRect = dropdownRef.current.getBoundingClientRect();
    const rect = triggerRef.current.getBoundingClientRect();
    const overflows = dropRect.bottom > window.innerHeight - 8;
    setCoords((prev) => ({
      ...prev,
      top: overflows ? rect.top - dropRect.height - 4 : rect.bottom + 4,
      ready: true,
    }));
  });

  // Reposition on scroll/resize while open (uses actual dropdown height)
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (!triggerRef.current || !dropdownRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const dropHeight = dropdownRef.current.offsetHeight;
      const left = Math.max(8, Math.min(
        rect.right - DROPDOWN_WIDTH,
        window.innerWidth - DROPDOWN_WIDTH - 8
      ));
      const up = window.innerHeight - rect.bottom < dropHeight + 8;
      setCoords({ top: up ? rect.top - dropHeight - 4 : rect.bottom + 4, left, ready: true });
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const handleTriggerClick = (e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  return (
    <div className="app-action-menu" ref={triggerRef}>
      <button
        type="button"
        className="app-action-trigger"
        onClick={handleTriggerClick}
      >
        {trigger}
      </button>
      {open && createPortal(
        <div
          ref={dropdownRef}
          className="app-action-dropdown"
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            zIndex: 9999,
            visibility: coords.ready ? "visible" : "hidden",
          }}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
};

export const ActionMenuItem = ({ children, onClick, danger = false }) => (
  <button
    type="button"
    className={`app-action-item${danger ? " app-action-item--danger" : ""}`}
    onClick={onClick}
  >
    {children}
  </button>
);

export const ActionMenuDivider = () => <div className="app-action-divider" />;

export default ActionMenu;
